import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import useUrlState from "@/hooks/useUrlState";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";

interface DeletionLog {
  id: number;
  user_id: string;
  deleted_count: number;
  method: "auto" | "manual";
  deleted_at: string;
}

interface LogsCache {
  page: number;
  logs: DeletionLog[];
  totalPages: number;
  totalCount: number;
  totalAuto: number;
  totalManual: number;
}

// 탭 복귀(리로드) 시 로딩 없이 마지막 삭제 로그를 즉시 보여주기 위한 캐시
const LOGS_CACHE_KEY = "cache:user_deletion_logs";

function readLogsCache(): LogsCache | null {
  try {
    const raw = sessionStorage.getItem(LOGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LogsCache;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeLogsCache(next: LogsCache): void {
  try {
    sessionStorage.setItem(LOGS_CACHE_KEY, JSON.stringify(next));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

export default function DeletionLogsPage() {
  const navigate = useNavigate();
  const { user, loading, session } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const pageSize = 15;

  // 페이지 번호를 주소(URL)에 남겨 새로고침·뒤로 가기·링크 공유에도 유지
  const [pageParam, setPageParam] = useUrlState("deletion_logs_page", "1");
  const page = useMemo(() => Math.max(1, parseInt(pageParam, 10) || 1), [pageParam]);

  const cachedRef = useRef<LogsCache | null>(readLogsCache());
  const initialCache = cachedRef.current && cachedRef.current.page === page ? cachedRef.current : null;

  const [logs, setLogs] = useState<DeletionLog[]>(initialCache?.logs ?? []);
  const [dbLoading, setDbLoading] = useState(initialCache === null);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(initialCache?.totalPages ?? 1);
  const [totalCount, setTotalCount] = useState(initialCache?.totalCount ?? 0);
  const [totalAuto, setTotalAuto] = useState(initialCache?.totalAuto ?? 0);
  const [totalManual, setTotalManual] = useState(initialCache?.totalManual ?? 0);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const fetchLogs = useCallback(
    async (targetPage: number) => {
      if (!user || !session) return;
      // 해당 페이지의 캐시가 있으면 로딩 화면 없이 조용히 갱신
      const hasCacheForPage = cachedRef.current !== null && cachedRef.current.page === targetPage;
      if (!hasCacheForPage) setDbLoading(true);
      setError(null);

      try {
        const { data, error: fnError } = await supabase.functions.invoke("get-deletion-logs", {
          body: { user_id: user.id, page: targetPage, pageSize },
        });

        if (fnError) {
          console.error("[DeletionLogs] Edge Function error:", fnError);
          if (!hasCacheForPage) setError("삭제 로그를 불러오는데 실패했습니다.");
          return;
        }

        if (data?.error) {
          console.error("[DeletionLogs] data error:", data.error);
          if (!hasCacheForPage) setError("삭제 로그를 불러오는데 실패했습니다.");
          return;
        }

        const nextLogs = data?.logs ?? [];
        const nextTotalPages = data?.totalPages ?? 1;
        const nextCount = data?.totalCount ?? 0;
        const nextAuto = data?.totalAuto ?? 0;
        const nextManual = data?.totalManual ?? 0;

        setLogs(nextLogs);
        setTotalPages(nextTotalPages);
        setTotalCount(nextCount);
        setTotalAuto(nextAuto);
        setTotalManual(nextManual);

        const nextCache: LogsCache = {
          page: targetPage,
          logs: nextLogs,
          totalPages: nextTotalPages,
          totalCount: nextCount,
          totalAuto: nextAuto,
          totalManual: nextManual,
        };
        writeLogsCache(nextCache);
        cachedRef.current = nextCache;

        // 페이지 수가 줄어 비어있는 페이지에 도착한 경우 안전한 페이지로 이동
        if (targetPage > nextTotalPages && nextTotalPages >= 1) {
          setPageParam(String(nextTotalPages));
        }
      } catch (err) {
        console.error("[DeletionLogs] fetch exception:", err);
        if (!hasCacheForPage) setError("삭제 로그를 불러오는데 실패했습니다.");
      } finally {
        setDbLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, session, pageSize]
  );

  // 사용자 준비되면 현재(주소에 있는) 페이지를 불러온다. 페이지가 바뀌면 자동으로 다시 불러온다.
  useEffect(() => {
    if (user && session) fetchLogs(page);
  }, [user, session, page, fetchLogs]);

  // 30일 지난 삭제 로그 자동 정리 (백그라운드)
  useEffect(() => {
    if (user && session) {
      supabase.rpc("cleanup_old_deletion_logs", { days_to_keep: 30 }).then(({ data, error }) => {
        if (!error && data?.deleted > 0) {
          console.log(`[DeletionLogs] ${data.deleted}건의 오래된 삭제 로그 정리 완료`);
        }
      });
    }
  }, [user, session]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setPageParam(String(newPage));
  };

  const totalDeleted = totalAuto + totalManual;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }
  if (!user) return null;

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen pb-20 md:pb-0`}>
        {/* Top bar */}
        <header className="h-14 md:h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => navigate("/dashboard/history")}
              className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base md:text-lg"></i>
            </button>
            <div>
              <h1 className="text-white font-semibold text-sm md:text-base">삭제 로그</h1>
              <p className="text-gray-500 text-[10px] md:text-xs hidden sm:block">자동/수동 삭제 기록 확인</p>
            </div>
          </div>
          <button
            onClick={() => fetchLogs(page)}
            disabled={dbLoading}
            className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors rounded-lg"
            title="새로고침"
          >
            <i className={`ri-refresh-line text-sm md:text-base ${dbLoading ? "animate-spin" : ""}`}></i>
          </button>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <div className="bg-gradient-to-br from-violet-500/15 to-violet-600/8 border border-white/[0.08] rounded-xl md:rounded-2xl p-3 md:p-5">
              <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1">총 삭제 건수</p>
              <p className="text-white text-lg md:text-xl font-bold">{totalDeleted.toLocaleString()}건</p>
              <p className="text-gray-500 text-[10px] md:text-xs mt-1">{totalCount.toLocaleString()}회 삭제됨</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500/12 to-emerald-600/6 border border-white/[0.08] rounded-xl md:rounded-2xl p-3 md:p-5">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-400 whitespace-nowrap">자동</span>
                <p className="text-gray-400 text-[10px] md:text-xs font-medium">24시간 자동 정리</p>
              </div>
              <p className="text-white text-lg md:text-xl font-bold">{totalAuto.toLocaleString()}건</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500/12 to-amber-600/6 border border-white/[0.08] rounded-xl md:rounded-2xl p-3 md:p-5">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-400 whitespace-nowrap">수동</span>
                <p className="text-gray-400 text-[10px] md:text-xs font-medium">사용자 직접 삭제</p>
              </div>
              <p className="text-white text-lg md:text-xl font-bold">{totalManual.toLocaleString()}건</p>
            </div>
          </div>

          {/* Logs table */}
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl md:rounded-2xl overflow-hidden">
            {dbLoading ? (
              <div className="py-12 md:py-16 flex flex-col items-center justify-center gap-2 md:gap-3">
                <div className="w-7 h-7 md:w-8 md:h-8 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin"></div>
                <p className="text-gray-500 text-xs md:text-sm">삭제 로그 불러오는 중...</p>
              </div>
            ) : error ? (
              <div className="py-12 md:py-16 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center mx-auto mb-3 rounded-full bg-red-500/10">
                  <i className="ri-error-warning-line text-red-400 text-xl md:text-2xl"></i>
                </div>
                <p className="text-red-400 text-sm font-medium mb-1">{error}</p>
                <button
                  onClick={() => fetchLogs(page)}
                  className="mt-3 inline-flex items-center gap-1.5 text-violet-400 hover:text-violet-300 text-xs font-medium cursor-pointer transition-colors"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  다시 시도
                </button>
              </div>
            ) : logs.length === 0 ? (
              <div className="py-16 md:py-20 text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-4 rounded-full bg-white/[0.04]">
                  <i className="ri-delete-bin-6-line text-gray-600 text-2xl md:text-3xl"></i>
                </div>
                <p className="text-gray-400 text-sm md:text-base font-medium mb-1">삭제 로그가 없어요</p>
                <p className="text-gray-600 text-xs md:text-sm">아직 삭제된 발송 내역이 없습니다</p>
              </div>
            ) : (
              <>
                {/* Table header - desktop */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/[0.06] bg-white/[0.02]">
                  <div className="col-span-4 text-left">
                    <span className="text-gray-500 text-xs font-medium">삭제 일시</span>
                  </div>
                  <div className="col-span-3 text-left">
                    <span className="text-gray-500 text-xs font-medium">방식</span>
                  </div>
                  <div className="col-span-3 text-left">
                    <span className="text-gray-500 text-xs font-medium">삭제 건수</span>
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-gray-500 text-xs font-medium">로그 ID</span>
                  </div>
                </div>

                {/* Table rows */}
                <div className="divide-y divide-white/[0.04]">
                  {logs.map((log) => {
                    const d = new Date(log.deleted_at);
                    const dateStr = d.toLocaleDateString("ko-KR", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    });
                    const timeStr = d.toLocaleTimeString("ko-KR", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: false,
                    });

                    return (
                      <div
                        key={log.id}
                        className="flex flex-col md:grid md:grid-cols-12 gap-1 md:gap-4 px-4 md:px-6 py-3 md:py-4 hover:bg-white/[0.02] transition-colors"
                      >
                        {/* Date/Time */}
                        <div className="md:col-span-4 flex items-center gap-2 md:gap-0">
                          <div className="w-5 h-5 flex items-center justify-center md:hidden shrink-0">
                            <i className="ri-calendar-line text-gray-600 text-xs"></i>
                          </div>
                          <div>
                            <p className="text-gray-300 text-xs md:text-sm font-medium">{dateStr}</p>
                            <p className="text-gray-600 text-[10px] md:text-xs">{timeStr}</p>
                          </div>
                        </div>

                        {/* Method badge */}
                        <div className="md:col-span-3 flex items-center">
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap ${
                              log.method === "auto"
                                ? "bg-emerald-500/15 text-emerald-400"
                                : "bg-amber-500/15 text-amber-400"
                            }`}
                          >
                            <i className={`${log.method === "auto" ? "ri-timer-line" : "ri-user-line"} text-[10px] md:text-xs`}></i>
                            {log.method === "auto" ? "자동 삭제" : "수동 삭제"}
                          </span>
                        </div>

                        {/* Count */}
                        <div className="md:col-span-3 flex items-center">
                          <p className="text-white text-sm md:text-base font-bold">
                            {log.deleted_count.toLocaleString()}건
                          </p>
                        </div>

                        {/* Log ID - desktop only */}
                        <div className="hidden md:flex md:col-span-2 items-center justify-end">
                          <span className="text-gray-600 text-xs font-mono">#{log.id}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Pagination */}
            {!dbLoading && !error && logs.length > 0 && totalPages > 1 && (
              <div className="flex items-center justify-between px-4 md:px-6 py-3 border-t border-white/[0.06]">
                <span className="text-gray-500 text-xs">
                  전체 {totalCount.toLocaleString()}건 중 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)}건
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <i className="ri-skip-left-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const pages: number[] = [];
                    let start = Math.max(1, page - 2);
                    let end = Math.min(totalPages, page + 2);
                    if (end - start < 4) {
                      if (start === 1) end = Math.min(totalPages, start + 4);
                      else start = Math.max(1, end - 4);
                    }
                    for (let i = start; i <= end; i++) pages.push(i);

                    return pages.map((p) => (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                          p === page
                            ? "bg-violet-500/20 text-violet-400"
                            : "text-gray-500 hover:text-white hover:bg-white/[0.06]"
                        }`}
                      >
                        {p}
                      </button>
                    ));
                  })()}

                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={page === totalPages}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <i className="ri-skip-right-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <MobileBottomNav />
      </main>
    </div>
  );
}